import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui'
import { useVerifyEmailMutation } from '../../services/authApi'
import { isNormalizedError } from '../../services/errorNormalizer'
import { AuthCard } from './AuthCard'
import { useSession } from './useSession'

type State = { phase: 'verifying' } | { phase: 'done' } | { phase: 'failed'; message: string }

const BROKEN_LINK = 'This verification link is not valid. Please open the latest email we sent you.'
const EXPIRED_LINK = 'This verification link has expired or is no longer valid. You can still sign in and book as usual.'

/**
 * OC-340: the "verify your email" link of the sign-up mail lands here,
 * `/{slug}/verify-email?id&hash&expires&signature`, instead of the admin login.
 * The page posts the four values back to the API once and then sends the customer
 * to sign in (or to their account when this browser is already signed in).
 */
export function VerifyEmailPage() {
  const { slug, isAuthenticated } = useSession()
  const [params] = useSearchParams()
  const [verifyEmail] = useVerifyEmailMutation()
  const [state, setState] = useState<State>({ phase: 'verifying' })
  // StrictMode runs effects twice in dev; the link should be spent once.
  const sent = useRef(false)

  useEffect(() => {
    if (sent.current) return
    sent.current = true

    const id = params.get('id') ?? ''
    const hash = params.get('hash') ?? ''
    const expires = params.get('expires') ?? ''
    const signature = params.get('signature') ?? ''
    if (!id || !hash || !expires || !signature) {
      setState({ phase: 'failed', message: BROKEN_LINK })
      return
    }

    verifyEmail({ slug, id, hash, expires, signature })
      .unwrap()
      .then(() => setState({ phase: 'done' }))
      .catch((e: unknown) => {
        // 403 is a bad or expired signature; anything else is worth a retry later.
        const status = isNormalizedError(e) ? e.status : null
        setState({
          phase: 'failed',
          message: status === 403 ? EXPIRED_LINK : isNormalizedError(e) ? e.message : BROKEN_LINK,
        })
      })
  }, [params, slug, verifyEmail])

  const next = isAuthenticated
    ? { to: `/${slug}/account`, label: 'Go to my account' }
    : { to: `/${slug}/signin`, label: 'Sign in' }

  if (state.phase === 'verifying') {
    return (
      <AuthCard icon="mark_email_read" title="Verifying your email" subtitle="One moment…">
        {null}
      </AuthCard>
    )
  }

  if (state.phase === 'done') {
    return (
      <AuthCard
        icon="check_circle"
        iconStyle="tile"
        title="Email verified"
        subtitle="Thank you. Your email address is confirmed and your account is ready to use."
      >
        <Button to={next.to} fullWidth size="lg">
          {next.label}
        </Button>
      </AuthCard>
    )
  }

  return (
    <AuthCard icon="error" iconStyle="tile" title="We could not verify your email" subtitle={state.message}>
      <Button to={next.to} fullWidth size="lg">
        {next.label}
      </Button>
    </AuthCard>
  )
}
